import {useEffect, useState} from 'react';

import type {ConversationOverview} from '../conversations/types';
import {useConversationClientPending} from '../../core/hooks/useConversationClientPending';
import {useHasUserMessage} from './useHasUserMessage';
import {
  useThreadsApp,
  useThreadsAuthoritativeConversationTitle,
  useThreadsConversationsController,
  useThreadsConversationViewModelIsEnded,
  useThreadsConversationViewModelState,
  useThreadsWithHistory,
} from '../app/hooks';

const FAST_POLL_INTERVAL_MS = 1_000;
const FAST_POLL_MAX_ATTEMPTS = 15;

export function useTitlePolling() {
  const withHistory = useThreadsWithHistory();
  const conversationTitle = useThreadsAuthoritativeConversationTitle();
  const {conversation, id: conversationId} =
    useThreadsConversationViewModelState();
  const app = useThreadsApp();
  const isEnded = useThreadsConversationViewModelIsEnded();
  const stopPolling = app.runtime === 'managed' && isEnded;
  const conversationViewModel =
    app.runtime === 'legacy' ? app.conversationViewModel : null;
  const historyViewModel =
    app.runtime === 'legacy' ? app.historyViewModel : null;
  const conversations = useThreadsConversationsController();
  const hasUserMessage = useHasUserMessage(conversation);
  const isClientBusy = useConversationClientPending(conversation);
  const [foundOverview, setFoundOverview] =
    useState<ConversationOverview | null>(null);

  useEffect(() => {
    setFoundOverview(null);
  }, [conversationId]);

  // Fast-poll for the generated title when the user has sent a message and no
  // title exists yet. Stops as soon as a title is found or after max attempts.
  // Title generation is server-gated on the history flag, so without history
  // there is never a title to poll for.
  useEffect(() => {
    if (!withHistory || !hasUserMessage || conversationTitle) return;
    if (stopPolling) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;

    const runPoll = async () => {
      if (cancelled) return;
      if (attempts >= FAST_POLL_MAX_ATTEMPTS) return;
      attempts += 1;

      const result = await conversations.list({
        conversationIds: [conversationId],
      });
      if (cancelled) return;

      if (result.type === 'success') {
        const overview = result.data[0];
        if (
          overview?.title &&
          (app.runtime === 'legacy' || overview.id === conversationId)
        ) {
          // The managed controller ingests the title in list().
          if (app.runtime === 'legacy') setFoundOverview(overview);
          return;
        }
      }

      timer = setTimeout(runPoll, FAST_POLL_INTERVAL_MS);
    };

    runPoll();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    app,
    stopPolling,
    withHistory,
    hasUserMessage,
    conversationTitle,
    conversationId,
    conversations,
  ]);

  // Sync the history list immediately. Defer the header title until streaming
  // finishes to avoid mid-stream pop-in (patchOverview is a no-op if unchanged).
  useEffect(() => {
    if (!foundOverview || !conversationViewModel || !historyViewModel) return;
    if (foundOverview.id !== conversationId) return;
    historyViewModel.patchOverview(conversationId, {
      title: foundOverview.title,
    });
    if (!isClientBusy) {
      conversationViewModel.patchOverview({title: foundOverview.title});
      setFoundOverview(null);
    }
  }, [
    foundOverview,
    isClientBusy,
    conversationId,
    historyViewModel,
    conversationViewModel,
  ]);
}
